import axios from 'axios';

import { ENDPOINTS } from '@/constants';

import { GraphqlRequestService } from '@/api/common/GraphqlRequestService';

import * as bff from '@/api/bff';
import * as bff_types from '@/api/bff_types';

import { BFF_ERROR_CODE_TYPES } from '@/constants/errors';
import { getErrorCodeFromResponse, getErrorMessageFromResponse } from './error';
import { AxiosBFFResponse, FailureBFFResponse, InterceptorBffResponse, SuccessBFFResponse } from '@/@types/common';

const GraphqlAxios = axios.create({
  baseURL: ENDPOINTS.GRAPHQL_BASE_URL(),
  timeout: 10000
});

GraphqlAxios.interceptors.response.use(
  (response: InterceptorBffResponse): AxiosBFFResponse => {
    if ('errors' in response.data) {
      return {
        ...response,
        data: createFailure(
          null,
          getErrorCodeFromResponse(response.data),
          getErrorMessageFromResponse(response.data)
        )
      };
    }

    return {
      ...response,
      data: createSuccess(response.data.data)
    };
  }
);

GraphqlRequestService.instance.setAxiosInstance(GraphqlAxios);

function createSuccess<T>(data: T): SuccessBFFResponse<T> {
  return {
    success: true,
    result: { data }
  };
}

function createFailure<T>(
  result: T,
  code: BFF_ERROR_CODE_TYPES,
  message: string
): FailureBFFResponse<T> {
  return {
    success: false,
    result,
    code,
    message
  };
}

export const getCostReimbursement = async (
  token: string,
  request: bff_types.PharmacyCostReimbursementRequestInput,
  paginationCriteria: bff_types.PaginationCriteriaInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request,
    paginationCriteria
  };
  return await bff.costReimbursementByPharmacist(param, headers);
};

export const getCostReimbursementSummary = async (
  token: string,
  request: bff_types.PharmacyCostReimbursementSummaryRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request
  };
  return await bff.costReimbursementSummaryByPharmacist(param, headers);
};

export const getShippingLabel = async (
  token: string,
  shippingLabelRequest: bff_types.ShippingLabelRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    shippingLabelRequest
  };
  return await bff.shippingLabelByPharmacist(param, headers);
};

export const postDeliveryReceipt = async (
  token: string,
  deliveryReceiptRequest: bff_types.DeliveryReceiptRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    deliveryReceiptRequest
  };
  return await bff.deliveryReceipt(param, headers);
};

export const postBePharmacyPresent = async (token: string) => {
  const headers = { Authorization: `Bearer ${token}` };
  return await bff.bePharmacyPresent(headers);
};

export const getPharmacyContactInformation = async (token: string) => {
  const headers = { Authorization: `Bearer ${token}` };
  return await bff.getPharmacyContactsInfomation(headers);
};

export const getPharmacyDetail = async (token: string) => {
  const headers = { Authorization: `Bearer ${token}` };
  return await bff.getPharmacyDetail(headers);
};

export const getDayOfWeek = async (
  date: string
) => {
  const param = {
    date
  }
  return await bff.getDayOfWeek(param);
}

export const getPharmacistDevices = async (token: string) => {
  const headers = { Authorization: `Bearer ${token}` };
  return await bff.getPharmacistDevices(headers);
};

export const addPharmacistDevices = async (
  token: string,
  request: bff_types.PharmacistDeviceAddMutationRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request
  }
  return await bff.addPharmacistDevice(param, headers);
};

export const updatePharmacyContacts = async (
  token: string,
  request: bff_types.PharmacyContactUpdateRequestInput[]
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request
  };
  return await bff.updatePharmacyContacts(param, headers);
};

export const postBePharmacyAbsent = async (token: string) => {
  const headers = { Authorization: `Bearer ${token}` };
  return await bff.bePharmacyAbsent(headers);
};

export const getCommerceOrderInfo = async (
  token: string,
  requestContextRequest: bff_types.DomainRequestContextRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const params = {
    requestContextRequest
  };
  return await bff.getCommerceOrdersByRequestContext(params, headers);
};

export const updatePriceByPharmacist = async (
  token: string,
  request: bff_types.OrderModifyPriceMutationRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request
  };
  return await bff.modifyPriceByPharmacist(param, headers);
};

export const resetOrderByPharmacist = async (
  token: string,
  request: bff_types.InitOrderByPharmacistMutationRequestInput
) => {
  const headers = { Authorization: `Bearer ${token}` };
  const param = {
    request
  };
  return await bff.orderResetByPharmacist(param, headers);
};

export const getEmergencyMessageReason = async (token?: string) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return (await bff.emergencyMessageReason(headers))?.getEmergencyMessageReason;
};

export const sendEmergencyMessage = (
  variables: {
    orderId: string;
    pharmacyPhone: string;
    reason: string;
    reasonId: string;
  },
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.emergencyMessage({ request: variables }, headers);
};

export const getSearchProductsByPharmacist = (token?: string) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.searchProductsByPharmacist(headers);
};

export const getAdBanner = (
  variables: bff_types.GetBannerAdExchangeVariables,
  token: string
) => {
  const headers = { Authorization: `Bearer ${token}` };
  return bff.getBannerAdExchange(variables, headers);
};

export const submitAdExposureLog = (
  variables: bff_types.AdExposureHandleMutationRequestInput,
  token?: string
) => {
  const headers = { Authorization: `Bearer ${token}` };
  return bff.handleAdExposure({ request: variables }, headers);
};

export const setCompletePartnershipPickupOrder = (
  variables: bff_types.SetCompletePartnershipPickupOrderVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.setCompletePartnershipPickupOrder(variables, headers);
};

export const getPharmacyTreatmentMedicineStructures = (
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.getPharmacyTreatmentMedicineStructures(headers);
}

export const registerPharmacyHandlingMedicines = (
  variables: bff_types.RegisterPharmacyHandlingMedicinesVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.registerPharmacyHandlingMedicines(variables, headers);
}

export const upsertPharmacyHandlingMedicines = (
  variables: bff_types.UpsertPharmacyHandlingMedicinesVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.upsertPharmacyHandlingMedicines(variables, headers);
}

export const getPharmacyHandlingMedicines = (
  variables: bff_types.GetPharmacyHandlingMedicinesVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.getPharmacyHandlingMedicines(variables, headers);
}

export const getPharmacyHandlingMedicine = (
  variables: bff_types.GetPharmacyHandlingMedicineVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.getPharmacyHandlingMedicine(variables, headers);
};

export const setMarkMedicineStandardAsSoldOut = (
  variables: bff_types.SetMarkMedicineStandardAsSoldOutVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.setMarkMedicineStandardAsSoldOut(variables, headers);
};

export const setMarkMedicineStandardAsAvailable = (
  variables: bff_types.SetMarkMedicineStandardAsAvailableVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.setMarkMedicineStandardAsAvailable(variables, headers);
};

export const setOrderPrice = (
  variables: bff_types.SetOrderPriceVariables,
  token?: string
) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return bff.setOrderPrice(variables, headers);
};

export const startDelivery = async (
  request: bff_types.OrderDeliveryStartMutationRequestInput,
  token: string
) => {
  const headers = {Authorization: `Bearer ${token}`};
  const param = {
    request
  };
  return await bff.startDelivery(param, headers);
}
